import { colors } from '@packages/core/styles';
import { Icon } from '@packages/ui/shared';
import { PublicationType } from '@web/vin-sessions/components/stis-publications/publication-type-icons';
import { StisFilterDropdown } from '@web/vin-sessions/components/stis-publications/stis-filter-dropdown';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import { countSdFilters, type SdCarlineOption, type SdFilters } from './sd-search.utils';

type Props = {
    carlineOptions: SdCarlineOption[];
    collapsed: boolean;
    /** Filters being edited. They reach the list when Search is pressed. */
    filters: SdFilters;
    modelYearOptions: string[];
    onChange: (filters: SdFilters) => void;
    onCollapsedChange: (collapsed: boolean) => void;
    /** Manuals the applied filters leave, shown in the bar as Publications does. */
    resultCount?: number;
};

const useStyles = createUseStyles({
    panel: {
        backgroundColor: colors.grayOne,
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 4,
    },
    body: {
        alignItems: 'stretch',
        display: 'flex',
        gap: 0,
        '@media (max-width: 1000px)': {
            flexDirection: 'column',
        },
    },
    typeColumn: {
        borderRight: `1px solid ${colors.grayThree}`,
        display: 'flex',
        flex: '0 1 auto',
        flexDirection: 'column',
        gap: 8,
        minWidth: 0,
        padding: '12px 16px',
        '@media (max-width: 1000px)': {
            borderBottom: `1px solid ${colors.grayThree}`,
            borderRight: 'none',
        },
    },
    vehicleColumn: {
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        gap: 8,
        minWidth: 0,
        padding: '12px 16px',
        '@media (max-width: 1000px)': {
            flex: '1 1 auto',
        },
    },
    /** Model Year and Carline sit on one line, as there are only the two of them. */
    vehicleFields: {
        alignItems: 'flex-end',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
    },
    columnTitle: {
        alignItems: 'center',
        color: colors.textDarkSecondary,
        display: 'flex',
        fontSize: 11.5,
        fontWeight: 700,
        gap: 6,
        letterSpacing: '0.6px',
        textTransform: 'uppercase',
    },
    lockedType: {
        alignItems: 'center',
        alignSelf: 'flex-start',
        backgroundColor: colors.white,
        border: `1px solid ${colors.blueTwo}`,
        borderRadius: 2,
        color: colors.textDarkPrimary,
        display: 'flex',
        fontSize: 14,
        gap: 8,
        padding: '5px 12px',
    },
    lockedNote: {
        color: colors.textDarkSecondary,
        fontSize: 12.5,
        lineHeight: '17px',
        whiteSpace: 'nowrap',
    },
    field: {
        display: 'flex',
        flex: '1 1 240px',
        flexDirection: 'column',
        gap: 6,
        minWidth: 200,
    },
    fieldLabel: {
        color: colors.textDarkSecondary,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.4px',
        textTransform: 'uppercase',
    },
    bar: {
        alignItems: 'center',
        borderTop: `1px solid ${colors.grayThree}`,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        padding: '8px 16px',
    },
    barButton: {
        alignItems: 'center',
        background: 'none',
        border: 'none',
        color: colors.blueOne,
        cursor: 'pointer',
        display: 'inline-flex',
        fontSize: 14,
        fontWeight: 500,
        gap: 6,
        padding: 0,
        '&:focus-visible': {
            outline: `2px solid ${colors.blueOne}`,
            outlineOffset: 2,
        },
    },
    appliedCount: {
        backgroundColor: colors.blueFour,
        borderRadius: 100,
        color: colors.blueOne,
        fontSize: 12,
        fontWeight: 700,
        minWidth: 20,
        padding: '0 6px',
        textAlign: 'center',
    },
    barEmpty: {
        color: colors.textDarkSecondary,
        fontSize: 14,
    },
    count: {
        color: colors.textDarkSecondary,
        fontSize: 14,
        marginLeft: 'auto',
        whiteSpace: 'nowrap',
    },
    countValue: {
        color: colors.textDarkPrimary,
        fontWeight: 700,
    },
});

export const SdCriteriaPanel: FC<Props> = ({
    carlineOptions,
    collapsed,
    filters,
    modelYearOptions,
    onChange,
    onCollapsedChange,
    resultCount,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const appliedCount = countSdFilters(filters);
    const yearOptions = useMemo(
        () => modelYearOptions.map((year) => ({ label: year, value: year })),
        [modelYearOptions]
    );
    const carlineDropdownOptions = useMemo(
        () => carlineOptions.map((carline) => ({ label: carline.description, value: carline.id })),
        [carlineOptions]
    );

    return (
        <section
            aria-label={t('stisPublications:serviceDiagnostics.filters', 'Narrow your search')}
            className={classes.panel}
        >
            {!collapsed && (
                <div className={classes.body}>
                    <div className={classes.typeColumn}>
                        <span className={classes.columnTitle}>
                            {t('publications:quickFilters.publicationType', 'Publication Type')}
                        </span>
                        <span className={classes.lockedType}>
                            <PublicationType publicationType="menuBook" size={22} />
                            {t('stisPublications:breadcrumbs.serviceDiagnostics', 'Service Diagnostics')}
                            <Icon color="blueOne" name="checkmark" size={16} />
                        </span>
                        <span className={classes.lockedNote}>
                            {t(
                                'stisPublications:serviceDiagnostics.onlyTypeNote',
                                'This page searches inside Service Diagnostics manuals.'
                            )}
                        </span>
                    </div>

                    <div className={classes.vehicleColumn}>
                        <span className={classes.columnTitle}>
                            <Icon color="blueOne" name="car-subaru" size={20} />
                            {t('publications:searchCriteria.vehicle', 'Vehicle')}
                        </span>

                        <div className={classes.vehicleFields}>
                            <div className={classes.field}>
                                <span className={classes.fieldLabel}>
                                    {t('publications:quickFilters.modelYear', 'Model Year')}
                                </span>
                                <StisFilterDropdown
                                    ariaLabel={t('publications:quickFilters.modelYear', 'Model Year')}
                                    onChange={(selected) => onChange({ carlineId: undefined, modelYear: selected[0] })}
                                    options={yearOptions}
                                    placeholder={t('stisPublications:serviceDiagnostics.anyYear', 'Select model year')}
                                    selected={filters.modelYear ? [filters.modelYear] : []}
                                    width="100%"
                                />
                            </div>

                            <div
                                className={classes.field}
                                title={
                                    filters.modelYear
                                        ? undefined
                                        : t(
                                              'stisPublications:serviceDiagnostics.pickYearTooltip',
                                              'Select a model year to select a carline'
                                          )
                                }
                            >
                                <span className={classes.fieldLabel}>
                                    {t('publications:quickFilters.carLine', 'Carline')}
                                </span>
                                <StisFilterDropdown
                                    ariaLabel={t('publications:quickFilters.carLine', 'Carline')}
                                    disabled={!filters.modelYear}
                                    onChange={(selected) => onChange({ ...filters, carlineId: selected[0] })}
                                    options={carlineDropdownOptions}
                                    placeholder={
                                        filters.modelYear
                                            ? t('stisPublications:serviceDiagnostics.anyCarline', 'Select carline')
                                            : t(
                                                  'stisPublications:serviceDiagnostics.pickYearFirst',
                                                  'Select a model year first'
                                              )
                                    }
                                    searchable
                                    selected={filters.carlineId ? [filters.carlineId] : []}
                                    width="100%"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className={classes.bar}>
                <button
                    aria-expanded={!collapsed}
                    className={classes.barButton}
                    onClick={() => onCollapsedChange(!collapsed)}
                    type="button"
                >
                    {collapsed ? (
                        <>
                            <Icon color="blueOne" name="filter-list" size={18} />
                            {t('publications:searchCriteria.expandFilters', 'Expand filters')}
                            {appliedCount > 0 && <span className={classes.appliedCount}>{appliedCount}</span>}
                            <Icon color="blueOne" name="chevron-down" size={16} />
                        </>
                    ) : (
                        <>
                            {t('publications:searchCriteria.collapse', 'Collapse')}
                            <Icon color="blueOne" name="chevron-up" size={16} />
                        </>
                    )}
                </button>

                {appliedCount === 0 && (
                    <span className={classes.barEmpty}>
                        {t('publications:searchCriteria.noFilters', 'No filters applied')}
                    </span>
                )}

                {resultCount !== undefined && (
                    <span className={classes.count}>
                        <span className={classes.countValue}>{resultCount.toLocaleString()}</span>{' '}
                        {t('stisPublications:serviceDiagnostics.manuals', 'manuals')}
                    </span>
                )}
            </div>
        </section>
    );
};
